import { Link } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { contributorTasks } from "@/features/contributor-tasks/contributor-tasks";
import { useApp } from "@/hooks/use-app-context";
import { normalizeUserRole } from "@/lib/auth/roles";
import { Icon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import { findContributorTask, type ContributionTaskKey } from "./task-registry";
import { useKitCopy } from "./use-kit-copy";

/**
 * The task name as a menu button: opens the list of every contribution
 * task (contributorTasks, same order and labels as the header and the
 * mobile app) to jump to another one.
 */
export function TaskSwitcher({ task, className }: { task: ContributionTaskKey; className?: string }) {
  const { copy, lang } = useKitCopy();
  const { user } = useApp();
  const current = findContributorTask(lang, task);
  const tasks = contributorTasks(lang, { collector: normalizeUserRole(user?.role) === "collector" });
  const primary = tasks.filter((entry) => entry.primary);
  const others = tasks.filter((entry) => !entry.primary);

  const item = (entry: (typeof tasks)[number]) => {
    const active = entry.key === task;
    return (
      <DropdownMenuItem key={entry.key} asChild className="min-h-11 gap-3">
        <Link to={entry.href} aria-current={active ? "page" : undefined}>
          <Icon name={entry.icon} className={cn("size-4", active ? "text-brand" : "text-muted-foreground")} />
          <span className="min-w-0 flex-1">
            <span className={cn("block text-sm", active ? "font-semibold text-foreground" : "text-foreground")}>{entry.label}</span>
            <span className="block truncate text-xs text-muted-foreground">{entry.hint}</span>
          </span>
          {active ? <Icon name="check" className="size-4 text-brand" /> : null}
        </Link>
      </DropdownMenuItem>
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "inline-flex h-11 min-w-0 items-center gap-2 rounded-md px-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-surface-2",
          className,
        )}
        aria-label={`${current.label}, ${copy.switchTask}`}
      >
        <span className="grid size-8 shrink-0 place-items-center rounded-md bg-brand/10 text-brand">
          <Icon name={current.icon} className="size-4" />
        </span>
        <span className="truncate text-lg font-semibold tracking-tight text-foreground">{current.label}</span>
        <Icon name="chevronDown" className="size-4 shrink-0 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">{copy.switchTask}</DropdownMenuLabel>
        {primary.map(item)}
        <DropdownMenuSeparator />
        {others.map(item)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
